import React, { useState } from 'react'
import {
  makeStyles,
  IconButton,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Button,
  Checkbox,
  Paper,
  TextField,
  InputAdornment,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Menu'
import SearchIcon from '@material-ui/icons/Search'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'

const useStyles = makeStyles((theme) => ({
  optionsContainer: {
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 1rem) calc(50% - 1rem)',
    columnGap: '2rem',
  },
  optionSearch: {
    width: '100%',
  },
  settingsDrawer: {
    padding: '1rem 1.5rem',
  },
  drawerOption: {
    width: 'max-content',
    marginBottom: '1rem',
  },
  columnOrderingOption: {
    margin: '1rem',
    padding: '1rem',
    whiteSpace: 'nowrap',
    width: 'max-content',
  },
  settingsResetButton: {
    bottom: '1rem',
    width: 'calc(100% - 4rem)',
    margin: 'auto',
    marginBottom: 0,
    border: '1px solid orange',
    height: '4rem',
    '&:hover': {
      backgroundColor: '#f500573d',
    },
  },
}))

const ColumnVisibilityAccordion = (props) => {
  const { classes, handleToggleColumnHidden, setAllHiddenState, columns } =
    props
  const [hiddenColumnMenuIsOpen, setHiddenColumnMenuIsOpen] = useState(true)
  const [search, setSearch] = useState('')

  const filteredColumns = search
    ? Object.values(columns).filter((col) => {
        return col.field.toLowerCase().includes(search.toLowerCase())
      })
    : Object.values(columns)

  return (
    <Accordion
      expanded={hiddenColumnMenuIsOpen}
      onChange={() => setHiddenColumnMenuIsOpen(!hiddenColumnMenuIsOpen)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        Visible Columns
      </AccordionSummary>
      <AccordionDetails>
        <div>
          <TextField
            size='small'
            className={classes.optionSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant='outlined'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.optionsContainer}>
            {filteredColumns.map((column, i) => {
              return (
                <div
                  key={`visibility option ${column.headerName}`}
                  className={classes.drawerOption}
                >
                  <Checkbox
                    color='primary'
                    checked={column.hide !== true}
                    onClick={() => handleToggleColumnHidden(i, column)}
                  />
                  {column.headerName}
                </div>
              )
            })}
          </div>
        </div>
      </AccordionDetails>
      {!search ? (
        <AccordionActions>
          <Button color='primary' onClick={() => setAllHiddenState(false)}>
            Show All
          </Button>
          <Button color='primary' onClick={() => setAllHiddenState(true)}>
            Hide All
          </Button>
        </AccordionActions>
      ) : null}
    </Accordion>
  )
}

const DraggableColumnOrdering = (props) => {
  const { className, columnName, displayText } = props
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: columnName })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <Paper
      className={className}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {displayText}
    </Paper>
  )
}

const ColumnOrderingAccordion = (props) => {
  const { columns, columnOrdering, setColumnOrdering, classes } = props
  const [expanded, setExpanded] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const columnOrderingOptions = showAll
    ? columnOrdering
    : columnOrdering.filter((colName) =>
        columns.find((col) => col.field === colName && col.hide !== true)
      )

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      style={{ marginBottom: '3rem' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        Column Ordering
      </AccordionSummary>
      <AccordionDetails>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={columnOrdering}
            strategy={rectSortingStrategy}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '50% 50%' }}>
              {columnOrderingOptions.map((columnField, i) => {
                const column = columns.find((col) => col.field === columnField)
                return (
                  <DraggableColumnOrdering
                    key={`column ordering ${column.field}`}
                    className={classes.columnOrderingOption}
                    columnName={column.field}
                    displayText={`${i + 1})${' '}${column.headerName}`}
                  />
                )
              })}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <Paper className={classes.columnOrderingOption}>
                {`${columns.find((col) => col.field === activeId).headerName}`}
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>
      </AccordionDetails>
      <AccordionActions>
        <Button color='primary' onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show only visible columns' : 'Show all columns'}
        </Button>
      </AccordionActions>
    </Accordion>
  )
  function handleDragStart(event) {
    setActiveId(event.active.id)
  }

  function handleDragEnd(event) {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = columnOrdering.indexOf(active.id)
      const newIndex = columnOrdering.indexOf(over.id)

      const newOrder = arrayMove(columnOrdering, oldIndex, newIndex)
      setColumnOrdering(newOrder)
    }

    setActiveId(null)
  }

  function handleDragCancel() {
    setActiveId(null)
  }
}

const Settings = (props) => {
  const {
    columns,
    updateColumn,
    setAllHiddenState,
    columnOrdering,
    setColumnOrdering,
    resetSettings,
  } = props
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const classes = useStyles()

  const handleToggleColumnHidden = (index, column) => {
    updateColumn(index, { ...column, hide: !column.hide })
  }

  return (
    <>
      <IconButton onClick={() => setMenuIsOpen(true)}>
        <SettingsIcon />
      </IconButton>
      <Drawer
        classes={{ paper: classes.settingsDrawer }}
        anchor='right'
        open={menuIsOpen}
        onClose={() => setMenuIsOpen(false)}
      >
        <ColumnVisibilityAccordion
          classes={classes}
          handleToggleColumnHidden={handleToggleColumnHidden}
          setAllHiddenState={setAllHiddenState}
          columns={columns}
        />
        <ColumnOrderingAccordion
          columns={columns}
          columnOrdering={columnOrdering}
          setColumnOrdering={setColumnOrdering}
          classes={classes}
        />
        <Button
          color='secondary'
          onClick={resetSettings}
          className={classes.settingsResetButton}
        >
          Reset Settings
        </Button>
      </Drawer>
    </>
  )
}

export default Settings
